import type { TransportResponse } from "./transport.ts";

/** Longest wait honoured from a `Retry-After`; a larger value is capped here. */
export const RETRY_AFTER_MAX_S = 10;
/** Tries in total (the first request included). */
export const BUSY_MAX_TRIES = 3;

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("aborted", "AbortError"));
    const done = () => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    };
    const timer = setTimeout(done, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });

/**
 * Repeats a request while the server says it is busy (503 + `Retry-After`, e.g. a cold disk cache):
 * waits as told (at most 10 s), tries at most 3 times, then hands back the last response. Other
 * statuses are never retried.
 */
export async function retryWhenBusy(
  send: () => Promise<TransportResponse>,
  signal?: AbortSignal,
): Promise<TransportResponse> {
  for (let attempt = 1; ; attempt++) {
    const res = await send();
    if (res.status !== 503 || attempt >= BUSY_MAX_TRIES) return res;
    const told = res.retryAfterSec;
    const s = told !== undefined && told !== null && Number.isFinite(told) ? told : 1;
    await sleep(Math.min(Math.max(s, 0), RETRY_AFTER_MAX_S) * 1000, signal);
  }
}
